import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getRequest } from "../../services/api";
import DashboardLayout from "../DashboardLayout/DashboardLayout";
import LoginModal from "../../components/Auth/LoginModel";
import "./MyTests.css";

interface UserTest {
  _id: string;
  test_id: string;
  test_name?: string;
  total_questions?: number;
  time_left?: number;
  test_status?: "started" | "submitted" | string;
  firstname?: string;
  blog_title_post_slug?: string;
}

type FilterTab = "all" | "started" | "submitted";

const MyTests: React.FC = () => {
  const [tests,          setTests]          = useState<UserTest[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab,      setActiveTab]      = useState<FilterTab>("all");
  const [searchQuery,    setSearchQuery]    = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTests = async () => {
      setLoading(true);
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) { setShowLoginModal(true); setLoading(false); return; }
        const userId = JSON.parse(userStr).id;
        const res = await getRequest("/auth/get-user-tests", { user_id: userId }, true);
        if (res?.data?.allTests) {
          setTests(res.data.allTests.map((t: any) => ({
            _id: t._id,
            test_id: t.test_id,
            test_name: t.test_name || t.blog_title_post_name || `Test ${t.test_id}`,
            total_questions: t.total_questions,
            time_left: t.time_left,
            test_status: t.test_status,
            firstname: t.firstname,
            blog_title_post_slug: t.blog_title_post_slug,
          })));
        }
      } catch (err) {
        console.error("Error fetching tests:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTests();
  }, []);

  const handleResume = (t: UserTest) => {
    navigate(`/online-tests/test-page/${t.blog_title_post_slug || t.test_id}/${t.test_id}`);
  };
  const handleViewResult = (t: UserTest) => {
    navigate(`/online-tests/test-result/${t.blog_title_post_slug || t.test_id}/${t.test_id}`);
  };

  const submitted = tests.filter(t => t.test_status === "submitted").length;
  const inProgress = tests.filter(t => t.test_status === "started").length;

  const filtered = tests
    .filter(t => activeTab === "all" || t.test_status === activeTab)
    .filter(t => !searchQuery || (t.test_name || "").toLowerCase().includes(searchQuery.toLowerCase()));

  const TABS: { id: FilterTab; label: string; count: number }[] = [
    { id: "all",       label: "All Tests",    count: tests.length },
    { id: "started",   label: "In Progress",  count: inProgress },
    { id: "submitted", label: "Completed",    count: submitted },
  ];

  return (
    <>
      <Helmet>
        <title>My Tests - Acadebility</title>
        <meta name="description" content="View and manage your started and submitted tests." />
      </Helmet>
      <DashboardLayout pageTitle="My Tests" activePath="/tests">

        {/* ── Summary stat pills ── */}
        <div className="mt-stat-pills">
          <div className="mt-stat-pill mt-pill-total">
            <span className="mt-pill-icon">📋</span>
            <div>
              <div className="mt-pill-val">{tests.length}</div>
              <div className="mt-pill-label">Total Tests</div>
            </div>
          </div>
          <div className="mt-stat-pill mt-pill-progress">
            <span className="mt-pill-icon">⏱️</span>
            <div>
              <div className="mt-pill-val">{inProgress}</div>
              <div className="mt-pill-label">In Progress</div>
            </div>
          </div>
          <div className="mt-stat-pill mt-pill-done">
            <span className="mt-pill-icon">✅</span>
            <div>
              <div className="mt-pill-val">{submitted}</div>
              <div className="mt-pill-label">Completed</div>
            </div>
          </div>
          <div className="mt-stat-pill mt-pill-rate">
            <span className="mt-pill-icon">📊</span>
            <div>
              <div className="mt-pill-val">
                {tests.length > 0 ? `${Math.round((submitted / tests.length) * 100)}%` : "—"}
              </div>
              <div className="mt-pill-label">Completion</div>
            </div>
          </div>
        </div>

        {/* ── Tests card ── */}
        <div className="dsh-card mt-card">

          {/* Tab bar + search */}
          <div className="mt-toolbar">
            <div className="mt-tabs">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  className={`mt-tab${activeTab === tab.id ? " mt-tab-active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                  type="button"
                >
                  {tab.label}
                  <span className="mt-tab-count">{tab.count}</span>
                </button>
              ))}
            </div>
            <div className="mt-search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                className="mt-search"
                placeholder="Search tests…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Test list */}
          {loading ? (
            <div className="mt-skeleton-list">
              {[1,2,3,4].map(i => (
                <div className="mt-skeleton-row" key={i}>
                  <div className="mt-skeleton-icon" />
                  <div className="mt-skeleton-lines">
                    <div className="mt-sk-line mt-sk-long" />
                    <div className="mt-sk-line mt-sk-short" />
                  </div>
                  <div className="mt-skeleton-btn" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="mt-empty">
              <span className="mt-empty-icon">🔍</span>
              <h3 className="mt-empty-title">
                {searchQuery ? "No tests match your search" : "No tests here yet"}
              </h3>
              <p className="mt-empty-desc">
                {searchQuery
                  ? "Try a different search term"
                  : "Start a test series and it will appear here"}
              </p>
              {!searchQuery && (
                <a href="/online-test-series" className="mt-empty-cta">Browse Test Series →</a>
              )}
            </div>
          ) : (
            <div className="mt-list">
              {filtered.map((t, idx) => (
                <div className="mt-test-row" key={t._id}>
                  {/* Left: index + status icon */}
                  <div className={`mt-row-status-icon mt-status-${t.test_status}`}>
                    {t.test_status === "submitted"
                      ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : t.test_status === "started"
                      ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/></svg>
                    }
                  </div>

                  {/* Middle: info */}
                  <div className="mt-row-info">
                    <div className="mt-row-num"># {idx + 1}</div>
                    <div className="mt-row-name">{t.test_name}</div>
                    <div className="mt-row-meta">
                      {t.total_questions != null && (
                        <span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                            <rect x="8" y="2" width="8" height="4" rx="1"/>
                          </svg>
                          {t.total_questions} Questions
                        </span>
                      )}
                      {t.time_left != null && t.test_status === "started" && (
                        <span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                          {Math.ceil(t.time_left / 60)} min left
                        </span>
                      )}
                      <span className={`mt-status-badge mt-badge-${t.test_status}`}>
                        {t.test_status === "submitted" ? "Completed" : t.test_status === "started" ? "In Progress" : t.test_status}
                      </span>
                    </div>
                  </div>

                  {/* Right: action button */}
                  <div className="mt-row-action">
                    {t.test_status === "started" ? (
                      <button className="mt-btn mt-btn-resume" onClick={() => handleResume(t)}>
                        Resume
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                      </button>
                    ) : t.test_status === "submitted" ? (
                      <button className="mt-btn mt-btn-result" onClick={() => handleViewResult(t)}>
                        View Result
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </button>
                    ) : (
                      <button className="mt-btn mt-btn-open" onClick={() => handleResume(t)}>
                        Open
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </DashboardLayout>

      {showLoginModal && (
        <LoginModal show={showLoginModal} onClose={() => setShowLoginModal(false)} onLoginSuccess={() => { setShowLoginModal(false); window.location.reload(); }} />
      )}
    </>
  );
};

export default MyTests;
